import { useState, useEffect } from 'react';

import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';

import { useToaster } from '@components/toaster';

import { CreateUserForm } from 'components/create-user-form';
import { UserModal } from 'components/UserModal';
import { useStore } from 'models/Provider';
import { User } from 'models/users';

export const UserModalEdit = observer(({ closeModal, user }) => {
  const { jobStore, userStore, t, api } = useStore();
  const { showError, showSuccess } = useToaster();
  const [disabled, setDisabled] = useState(false);
  const [editing, setEditing] = useState(userStore.editing);
  const jobs = jobStore.notArchived;

  useEffect(() => {
    if (disabled) return;
    userStore.setEditing(User.create(getSnapshot(user)));
    userStore.editing.setFollowingJobs(user.followingJobs.toJSON());
    setEditing(userStore.editing);
    return () => userStore.setEditing(null);
  }, [user.followingJobs, disabled, user, userStore]);

  async function save() {
    setDisabled(true);
    let data = {};
    if (user.role !== editing.role) {
      data.role = editing.role;
    }
    if (user.addToNewJobs !== editing.addToNewJobs) {
      data.addToNewJobs = editing.addToNewJobs;
    }
    if (user.addToHiredApplications !== editing.addToHiredApplications) {
      data.addToHiredApplications = editing.addToHiredApplications;
    }
    if (Object.keys(data).length > 0) {
      const res = await api.users.updateDetails(user.id, data);
      if (res.error) {
        editing.setErrors(res.data);
        setDisabled(false);
        showError(t('user.edit.update.errors'));
        return;
      } else {
        showSuccess(t('user.edit.update.success'));
        user.setRole(editing.role);
        user.setAddToNewJobs(editing.addToNewJobs);
        user.setAddToHiredApplications(editing.addToHiredApplications);
      }
    }
    await Promise.all(
      user.followingJobs.map(async (job) => {
        if (!editing.followingJobs.includes(job)) {
          await user.revokeJobAccess(job, true);
        }
      }),
    );
    await Promise.all(
      editing.followingJobs.map(async (job) => {
        if (!user.followingJobs.includes(job)) {
          await user.grantJobAccess(job, true);
        }
      }),
    );
    closeModal();
  }
  function isDisabled() {
    if (disabled || !editing) {
      return true;
    }
    if (
      user.role !== editing.role ||
      user.addToNewJobs !== editing.addToNewJobs ||
      user.addToHiredApplications !== editing.addToHiredApplications
    ) {
      return false;
    }

    if (user.followingJobs.length === editing.followingJobs.length) {
      const all = user.followingJobs.every((job) => editing.followingJobs.includes(job));
      return all;
    } else {
      return false;
    }
  }

  if (!editing) return null;

  return (
    <UserModal title={t('user.edit.heading')}>
      <CreateUserForm
        allowSend={false}
        user={editing}
        close={closeModal}
        jobs={jobs}
        save={save}
        errors={editing.errors}
        disabled={isDisabled()}
        editing
      />
    </UserModal>
  );
});
